"use client";

import React, { useMemo } from "react";
import { m } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Badge from "@mui/material/Badge";
import Drawer from "@mui/material/Drawer";
import SvgIcon from "@mui/material/SvgIcon";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";

import { Iconify } from "@onair/minimal-ui/iconify";
import { varHover } from "@onair/minimal-ui/animate";
import { Scrollbar } from "@onair/minimal-ui/scrollbar";
import { useBoolean } from "@onair/minimal-ui/hooks/use-boolean";
import { LoadingScreen } from "@onair/minimal-ui/loading-screen";

import { actionMaskAsReadAll, useNotiStore } from "@onair/store/notifications";
import { NotificationList } from "@onair/ui/notifications/notification-list";
import {
	NotificationTabs,
	NotificationRoleTab,
} from "@onair/ui/notifications/notification-tabs";
import { NotificationVariant } from "@onair/ui/notifications/notification-item";
import { NotificationEmpty } from "@onair/ui/notifications/notification-empty";

import { Button } from "@mui/material";
import { useGetNotificationQuery } from "@onair/operations";
interface ButtonNotificationProps {}

const LIMIT = 50;

export const ButtonNotification = ({ ...other }: ButtonNotificationProps) => {
	const drawer = useBoolean();
	const [tabKey, setTabKey] = React.useState<NotificationRoleTab>(
		NotificationRoleTab.ALL,
	);
	const queryClient = useQueryClient();
	const notiStore = useNotiStore();

	const { isLoading, isFetching, data, refetch } = useGetNotificationQuery({
		role:
			tabKey === NotificationRoleTab.HOST
				? "HOST"
				: tabKey === NotificationRoleTab.USER
					? "USER"
					: undefined,
		limit: LIMIT,
		enabled: drawer.value,
	});

	const notifications = data?.data;

	const handleMarkAllAsRead = React.useCallback(async () => {
		await actionMaskAsReadAll();
		queryClient.invalidateQueries({ queryKey: ["notifications"] });
		queryClient.invalidateQueries({ queryKey: ["notifications-count"] });
	}, []);

	const handleChange = (
		event: React.SyntheticEvent,
		newValue: NotificationRoleTab,
	) => {
		setTabKey(newValue);
	};

	const handleViewAll = () => {
		window.location.href = "/notifications";
	};

	return (
		<>
			<IconButton
				component={m.button}
				whileTap="tap"
				whileHover="hover"
				variants={varHover(1.05)}
				onClick={drawer.onTrue}
				{...other}
			>
				<Badge badgeContent={notiStore.total} color="error">
					<SvgIcon>
						<path
							fill="currentColor"
							d="M18.75 9v.704c0 .845.24 1.671.692 2.374l1.108 1.723c1.011 1.574.239 3.713-1.52 4.21a25.794 25.794 0 0 1-14.06 0c-1.759-.497-2.531-2.636-1.52-4.21l1.108-1.723a4.393 4.393 0 0 0 .693-2.374V9c0-3.866 3.022-7 6.749-7s6.75 3.134 6.75 7"
							opacity="0.5"
						/>
						<path
							fill="currentColor"
							d="M12.75 6a.75.75 0 0 0-1.5 0v4a.75.75 0 0 0 1.5 0zM7.243 18.545a5.002 5.002 0 0 0 9.513 0c-3.145.59-6.367.59-9.513 0"
						/>
					</SvgIcon>
				</Badge>
			</IconButton>
			<Drawer
				open={drawer.value}
				onClose={drawer.onFalse}
				anchor="right"
				slotProps={{ backdrop: { invisible: true } }}
				PaperProps={{
					sx: { width: 1, maxWidth: 420, overflowY: "unset" },
				}}
			>
				<Stack
					direction="row"
					alignItems="center"
					sx={{
						py: 2,
						pl: 2.5,
						pr: 1,
						minHeight: 68,
						borderBottom: "1px solid #919EAB33",
					}}
				>
					<Typography variant="h6" sx={{ flexGrow: 1 }}>
						Thông báo
					</Typography>

					{!!notiStore.total && (
						<Tooltip title="Đánh dấu đã đọc tất cả">
							<IconButton color="primary" onClick={handleMarkAllAsRead}>
								<Iconify icon="eva:done-all-fill" />
							</IconButton>
						</Tooltip>
					)}

					<IconButton
						onClick={drawer.onFalse}
						sx={{ display: { xs: "inline-flex", sm: "none" } }}
					>
						<Iconify icon="mingcute:close-line" />
					</IconButton>
				</Stack>
				<NotificationTabs
					enabled={drawer.value}
					value={tabKey}
					onChange={handleChange}
				/>
				{(isLoading || isFetching) && (
					<Box
						width="100%"
						justifyContent="center"
						alignItems="center"
						height="100%"
						position="absolute"
					>
						<LoadingScreen />
					</Box>
				)}
				<Box
					sx={{
						height: "100%",
						display: "flex",
						flexDirection: "column",
					}}
				>
					<Scrollbar
						sx={{
							maxHeight: "calc(100vh - 166px)",
						}}
					>
						{notifications?.total === 0 ? (
							<NotificationEmpty />
						) : (
							<NotificationList
								variant={NotificationVariant.CONTAINED}
								items={notifications?.items || []}
							/>
						)}
					</Scrollbar>
					<Button
						sx={{
							width: "100%",
							borderRadius: 0,
							py: 1.5,
						}}
						onClick={handleViewAll}
					>
						Xem tất cả
					</Button>
				</Box>
			</Drawer>
		</>
	);
};
